import { useMemo } from 'react';
import { useQuery } from '@apollo/client/react/hooks';
import { DeviceMode } from '@oolio-group/domain';
import {
  GET_DOCKET_FILTER_DEVICES,
  GET_DOCKET_FILTER_SECTIONS,
} from '../graphql/docketFilter';
import { noopHandler } from '../utils/general';
import { FilterOption } from '../utils/docketFilter';
import { createNameComparator, sortByName } from '../utils/sorting';
import { useSession } from './useSession';

// ORB never sends orders; a missing mode on older devices means F_POS
const canCreateOrders = (mode?: string | null) =>
  (mode ?? DeviceMode.F_POS) !== DeviceMode.ORB;

export interface DocketFilterOptions {
  // undefined until the first response, so callers can tell "not loaded" from "none"
  devices?: FilterOption[];
  sections?: FilterOption[];
  loading: boolean;
}

// Loads the devices and sections the user can pick from in Settings, sorted by name.
export function useDocketFilterOptions({
  skip = false,
}: { skip?: boolean } = {}): DocketFilterOptions {
  const { session } = useSession();
  const storeId = session?.currentStore?.id;

  const { data: deviceData, loading: devicesLoading } = useQuery<{
    devices: ((FilterOption & { mode?: string | null }) | null)[] | null;
  }>(GET_DOCKET_FILTER_DEVICES, {
    skip,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    onError: noopHandler,
  });

  const { data: sectionData, loading: sectionsLoading } = useQuery<{
    sectionsByStore: (FilterOption | null)[] | null;
  }>(GET_DOCKET_FILTER_SECTIONS, {
    variables: { storeId },
    skip: skip || !storeId,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    onError: noopHandler,
  });

  const compareNames = useMemo(
    () => createNameComparator(session?.localeCode),
    [session?.localeCode],
  );

  const devices = useMemo(() => {
    const list = deviceData?.devices;
    if (!list) return undefined;
    return sortByName(
      list
        .filter(
          (d): d is NonNullable<typeof d> => !!d && canCreateOrders(d.mode),
        )
        .map(d => ({ id: d.id, name: d.name })),
      compareNames,
    );
  }, [deviceData, compareNames]);

  const sections = useMemo(() => {
    const list = sectionData?.sectionsByStore;
    if (!list) return undefined;
    return sortByName(
      list
        .filter((s): s is NonNullable<typeof s> => !!s)
        .map(s => ({ id: s.id, name: s.name })),
      compareNames,
    );
  }, [sectionData, compareNames]);

  return useMemo(
    () => ({ devices, sections, loading: devicesLoading || sectionsLoading }),
    [devices, sections, devicesLoading, sectionsLoading],
  );
}
